import { useCallback, useState } from 'react'
import { useAppDispatch } from '@/store/hooks'
import { attachJob } from '@/features/ai/jobsSlice'
import { useStartImageGenerationMutation } from '@/features/ai/studioApi'
import { parseStartError, type StartFailure } from '@/features/ai/lib/parseStartError'

/**
 * Запуск отрисовки картинок. Отдельный хук, а не ветка `useStartGeneration`:
 * тот жёстко про `course_generation` — другое тело, другой ответ, другой исход
 * и другая запись реестра.
 *
 * Два режима: авто (`targets: undefined`/`null` → тело `targets: null`) —
 * «нарисовать всё, чего нет в исходном слоте курса», и явный список ключей
 * целей (под-проект D2) — тело несёт его как есть.
 */
export const useStartImageGeneration = () => {
  const dispatch = useAppDispatch()
  const [startImages, { isLoading }] = useStartImageGenerationMutation()
  const [failure, setFailure] = useState<StartFailure | null>(null)

  const start = useCallback(
    async (courseId: number, targets: string[] | null = null, pilotSets = 0): Promise<boolean> => {
      setFailure(null)
      try {
        const started = await startImages({
          course_id: courseId,
          targets,
          // Поле шлём ВСЕГДА явно: дефолт на сервере существует ради тех, кто
          // зовёт ручку напрямую.
          pilot_sets: pilotSets,
        }).unwrap()
        // `attachJob` сам чистит историю прошлого прогона — инвариант живёт в
        // `chatSlice`, своей чистки под-проекты D-F не заводят. Только после
        // успешного ответа: упавший старт не имеет права стереть итог,
        // который автор ещё не прочитал.
        dispatch(
          attachJob({
            courseId,
            entry: {
              jobId: started.job_id,
              type: 'course_image_generation',
              startedAt: Date.now(),
              // Джоба не возвращает параметры запуска (§1.2) — храним у себя:
              // итог прогона обязан уметь сказать, что именно рисовали.
              params: { kind: 'course_image_generation', targets, pilot_sets: pilotSets },
              // Знаменатель прогресса до первого отчёта воркфлоу: `result`
              // ещё null, а размер плана уже известен из ответа 201.
              total: started.targets_total ?? null,
            },
          }),
        )
        return true
      } catch (error) {
        setFailure(parseStartError(error))
        return false
      }
    },
    [startImages, dispatch],
  )

  return { start, isStarting: isLoading, failure, reset: () => setFailure(null) }
}
